import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { RegMark } from "./BlueprintBg";
import { OLD_DOMAIN, NEW_DOMAIN, LAUNCH_DATE } from "../config";

const lineReveal = {
  hidden: { y: "108%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.12 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.8 + i * 0.1 },
  }),
};

const Barcode = () => (
  <div
    className="h-10 w-full"
    aria-hidden="true"
    style={{
      backgroundImage:
        "repeating-linear-gradient(to right, var(--xp-text) 0, var(--xp-text) 2px, transparent 2px, transparent 4px, var(--xp-text) 4px, var(--xp-text) 5px, transparent 5px, transparent 9px, var(--xp-text) 9px, var(--xp-text) 12px, transparent 12px, transparent 14px)",
    }}
  />
);

export const Hero = () => {
  const { t } = useLanguage();
  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];
  const launchStr = LAUNCH_DATE.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  }).toUpperCase();

  return (
    <section
      id="top"
      className="relative min-h-screen border-b bp-border pt-16"
      data-testid="hero-section"
    >
      {/* top metadata strip */}
      <motion.div
        variants={fadeUp}
        custom={0}
        initial="hidden"
        animate="visible"
        className="flex flex-wrap items-center justify-between gap-2 border-b bp-border px-4 py-3 font-mono-x text-[10px] uppercase tracking-[0.2em] text-muted-x md:px-8"
      >
        <span className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 animate-blink"
            style={{ background: "var(--xp-accent)" }}
          />
          {t.hero.manifest} / {launchStr}
        </span>
        <span className="hidden sm:block">JKT 6.2°S · 106.8°E</span>
        <span>DOC.NO — XPDC-2026-0721</span>
      </motion.div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-0 px-4 md:px-8 lg:grid-cols-12">
        {/* LEFT: poster headline */}
        <div className="relative col-span-1 py-14 md:py-24 lg:col-span-7 lg:pr-10">
          <RegMark className="left-0 top-8" />

          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="visible"
            className="mb-6 inline-flex items-center gap-3 border bp-border px-4 py-2 font-mono-x text-[10px] uppercase tracking-[0.25em]"
            data-testid="hero-badge"
          >
            <span style={{ color: "var(--xp-accent)" }}>●</span>
            {t.hero.badge}
          </motion.div>

          <h1 className="font-display text-6xl uppercase leading-[0.86] tracking-tighter sm:text-7xl lg:text-[7.5rem]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="visible"
                  className="block py-0.5"
                  style={i === 2 ? { color: "var(--xp-primary)" } : undefined}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className="mt-8 max-w-lg text-base leading-relaxed text-muted-x md:text-lg"
            data-testid="hero-desc"
          >
            {t.hero.desc}
          </motion.p>

          <motion.a
            href="#countdown"
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate="visible"
            className="group mt-10 inline-flex items-center gap-3 font-mono-x text-xs uppercase tracking-[0.2em] text-muted-x"
            data-testid="scroll-cue"
          >
            <span className="flex h-9 w-9 items-center justify-center border bp-border transition-colors duration-300 group-hover:bg-[var(--xp-primary)] group-hover:text-white">
              <ArrowDown className="h-4 w-4" />
            </span>
            {t.hero.scroll}
          </motion.a>
        </div>

        {/* RIGHT: waybill / manifest card */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative col-span-1 flex items-center border-t bp-border py-14 lg:col-span-5 lg:border-l lg:border-t-0 lg:py-24 lg:pl-10"
          data-testid="hero-waybill"
        >
          <div
            className="w-full border bp-border"
            style={{ background: "var(--xp-surface)" }}
          >
            <div className="flex items-center justify-between border-b bp-border px-5 py-3 font-mono-x text-[10px] uppercase tracking-[0.2em] text-muted-x">
              <span>WAYBILL</span>
              <span>REV. 02</span>
            </div>

            <div className="grid grid-cols-2 divide-x bp-border border-b bp-border">
              <div className="p-5">
                <p className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-muted-x">
                  {t.hero.migrating}
                </p>
                <p className="mt-2 font-mono-x text-sm line-through opacity-60">
                  {OLD_DOMAIN}
                </p>
              </div>
              <div className="p-5">
                <p className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-muted-x">
                  {t.hero.to}
                </p>
                <p
                  className="mt-2 font-mono-x text-sm font-bold"
                  style={{ color: "var(--xp-primary)" }}
                >
                  {NEW_DOMAIN}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 border-b bp-border px-5 py-4">
              <ArrowRight
                className="h-5 w-5 shrink-0"
                style={{ color: "var(--xp-accent)" }}
              />
              <p className="font-display text-lg uppercase tracking-tight">
                {t.hero.era}
              </p>
              <span className="ml-auto font-mono-x text-[10px] uppercase tracking-[0.2em] text-muted-x">
                ETA {launchStr}
              </span>
            </div>

            <div className="px-5 py-5">
              <Barcode />
              <p className="mt-2 text-center font-mono-x text-[10px] tracking-[0.3em] text-muted-x">
                XPDC · 2026 · 0721 · ID
              </p>
            </div>
          </div>

          <RegMark className="bottom-8 right-0" />
        </motion.div>
      </div>
    </section>
  );
};
